import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import {
    FaCube, FaCloudUploadAlt, FaDownload, FaTrash, FaCopy, FaCheck,
    FaShareAlt, FaClock, FaCheckCircle, FaSpinner, FaExclamationTriangle,
    FaFileArchive, FaExternalLinkAlt, FaInfoCircle, FaEye, FaTooth,
    FaPlus, FaExchangeAlt
} from 'react-icons/fa';
import STLViewerModal, { canViewFile } from './STLViewerModal';
import '../styles/Case3DFiles.css';

const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Mo';
    const mb = bytes / (1024 * 1024);
    if (mb < 0.1) {
        return `${(bytes / 1024).toFixed(1)} Ko`;
    }
    return `${mb.toFixed(1)} Mo`;
};

const generateToken = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let res = 'eno-';
    for (let i = 0; i < 8; i++) {
        res += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return res;
};

// Détection automatique de la catégorie selon le nom du fichier
const detectCategory = (fileName = '') => {
    const lower = (fileName || '').toLowerCase();
    if (lower.includes('maxill') || lower.includes('upper') || lower.includes('haut')) {
        return 'maxillaire';
    }
    if (lower.includes('mandib') || lower.includes('lower') || lower.includes('bas')) {
        return 'mandibule';
    }
    if (lower.includes('occlus') || lower.includes('bite') || lower.includes('articul')) {
        return 'occlusion';
    }
    return 'autres';
};

const CATEGORY_META = {
    maxillaire: {
        label: 'Haut (Maxillaire)',
        short: 'Haut',
        badgeClass: 'bg-primary-subtle text-primary border border-primary-subtle',
        icon: '🦷',
        color: '#2563eb'
    },
    mandibule: {
        label: 'Bas (Mandibule)',
        short: 'Bas',
        badgeClass: 'bg-purple-subtle text-purple border border-purple-subtle',
        icon: '🦷',
        color: '#9333ea',
        style: { backgroundColor: '#f3e8ff', color: '#7e22ce', borderColor: '#d8b4fe' }
    },
    occlusion: {
        label: 'Occlusion',
        short: 'Occlusion',
        badgeClass: 'bg-warning-subtle text-warning-emphasis border border-warning-subtle',
        icon: '⚡',
        color: '#d97706',
        style: { backgroundColor: '#fef3c7', color: '#b45309', borderColor: '#fde68a' }
    },
    autres: {
        label: 'Autre fichier',
        short: 'Autre',
        badgeClass: 'bg-secondary-subtle text-secondary border border-secondary-subtle',
        icon: '📁',
        color: '#64748b'
    }
};

function Case3DFiles({ order, isAdmin = true, isProducer = false }) {
    const [files, setFiles] = useState([]);
    const [transfer, setTransfer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadTargetCat, setUploadTargetCat] = useState('');
    const [copied, setCopied] = useState(false);
    const [tableError, setTableError] = useState(false);
    const [dragActiveZone, setDragActiveZone] = useState(null); // 'maxillaire' | 'mandibule' | 'occlusion' | 'global'
    const [viewerFile, setViewerFile] = useState(null);

    const fileInputRefGlobal = useRef(null);
    const fileInputRefMaxilla = useRef(null);
    const fileInputRefMandible = useRef(null);
    const fileInputRefOcclusion = useRef(null);

    const orderId = order?.id;

    // Load existing transfer and files
    useEffect(() => {
        if (!orderId) return;
        loadTransferData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderId]);

    const loadTransferData = async () => {
        try {
            setLoading(true);
            setTableError(false);

            // 1. Fetch transfer record from database
            const { data: transferData, error: transferErr } = await supabase
                .from('case_transfers')
                .select('*')
                .eq('case_id', orderId)
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle();

            if (transferErr && (transferErr.code === '42P01' || transferErr.message?.includes('does not exist'))) {
                console.warn("Table case_transfers n'existe pas encore. Veuillez lancer setup_transfers.sql.");
                setTableError(true);
            }

            // 2. Also check files in storage bucket directly
            let storageItems = [];
            try {
                const { data: storageFiles } = await supabase.storage
                    .from('stl-files')
                    .list(`orders/${orderId}`, { limit: 100 });

                if (storageFiles && storageFiles.length > 0) {
                    storageItems = storageFiles
                        .filter(f => f.name && f.id !== null && f.name.includes('.'))
                        .map(f => {
                            const path = `orders/${orderId}/${f.name}`;
                            const { data: urlData } = supabase.storage.from('stl-files').getPublicUrl(path);
                            return {
                                name: f.name,
                                path: path,
                                size: f.metadata?.size || 0,
                                url: urlData?.publicUrl || '',
                                category: detectCategory(f.name),
                                uploadedAt: f.created_at || new Date().toISOString()
                            };
                        });
                }
            } catch (e) {
                console.warn('Erreur lecture storage:', e);
            }

            if (transferData) {
                setTransfer(transferData);
                const dbFiles = Array.isArray(transferData.files) ? transferData.files : [];
                // Merge any files that might be in storage but not yet in the DB array
                const merged = [...dbFiles];
                storageItems.forEach(si => {
                    if (!merged.some(m => m.name === si.name || m.path === si.path)) {
                        merged.push(si);
                    }
                });

                // S'assurer que chaque fichier a son chemin, son URL et sa catégorie assignée
                const enriched = merged.map(f => {
                    const filePath = f.path || `orders/${orderId}/${f.name}`;
                    const fileUrl = f.url || supabase.storage.from('stl-files').getPublicUrl(filePath).data?.publicUrl || '';
                    const cat = f.category || detectCategory(f.name);
                    return { ...f, path: filePath, url: fileUrl, category: cat };
                });

                setFiles(enriched);
                if (enriched.length !== dbFiles.length || enriched.some((ef, i) => !dbFiles[i]?.category)) {
                    await syncTransferRecord(enriched, transferData);
                }
            } else {
                setFiles(storageItems);
                if (storageItems.length > 0) {
                    await syncTransferRecord(storageItems, null);
                }
            }
        } catch (err) {
            console.error('Erreur chargement transferts 3D:', err);
        } finally {
            setLoading(false);
        }
    };

    const syncTransferRecord = async (newFileList, existingRecord = null) => {
        if (!orderId) return null;
        try {
            let activeRecord = existingRecord || transfer;
            if (!activeRecord?.id) {
                const { data: found } = await supabase
                    .from('case_transfers')
                    .select('*')
                    .eq('case_id', orderId)
                    .order('created_at', { ascending: false })
                    .limit(1)
                    .maybeSingle();
                if (found) {
                    activeRecord = found;
                }
            }

            const currentToken = activeRecord?.token || generateToken();
            let saved = null;

            if (activeRecord?.id) {
                const { data, error } = await supabase
                    .from('case_transfers')
                    .update({
                        files: newFileList,
                        token: currentToken,
                        updated_at: new Date().toISOString()
                    })
                    .eq('id', activeRecord.id)
                    .select()
                    .single();

                if (error) throw error;
                saved = data;
            } else {
                const { data, error } = await supabase
                    .from('case_transfers')
                    .insert([{
                        case_id: orderId,
                        token: currentToken,
                        files: newFileList,
                        download_count: 0
                    }])
                    .select()
                    .single();

                if (error) throw error;
                saved = data;
            }

            if (saved) {
                setTransfer(saved);
                return saved;
            }
            return null;
        } catch (err) {
            console.error('Sync transfer record error:', err);
            return null;
        }
    };

    const handleUploadFiles = async (fileList, targetCategory = null) => {
        if (!fileList || fileList.length === 0) return;
        const validFiles = Array.from(fileList);

        try {
            setUploading(true);
            setUploadTargetCat(targetCategory ? CATEGORY_META[targetCategory]?.label : 'Général');
            setUploadProgress(10);

            const uploadedItems = [];

            for (let i = 0; i < validFiles.length; i++) {
                const file = validFiles[i];
                const cleanName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
                const filePath = `orders/${orderId}/${Date.now()}_${cleanName}`;

                const { error: upErr } = await supabase.storage
                    .from('stl-files')
                    .upload(filePath, file, { upsert: true });

                if (upErr) {
                    console.error('Erreur upload:', upErr);
                    alert(`Échec de l'envoi de ${file.name}. Assurez-vous que le bucket "stl-files" est créé.`);
                    continue;
                }

                const { data: urlData } = supabase.storage
                    .from('stl-files')
                    .getPublicUrl(filePath);

                // Assigner la catégorie ciblée par la zone ou auto-détecter
                const finalCategory = targetCategory || detectCategory(file.name);

                uploadedItems.push({
                    name: file.name,
                    path: filePath,
                    size: file.size,
                    type: file.type || 'application/octet-stream',
                    url: urlData?.publicUrl || '',
                    category: finalCategory,
                    uploadedAt: new Date().toISOString()
                });

                setUploadProgress(Math.round(((i + 1) / validFiles.length) * 90));
            }

            const updatedFiles = [...files, ...uploadedItems];
            setFiles(updatedFiles);
            await syncTransferRecord(updatedFiles);

            setUploadProgress(100);
        } catch (err) {
            console.error('Erreur upload fichiers 3D:', err);
            alert("Erreur lors de l'envoi des fichiers.");
        } finally {
            setUploading(false);
            setUploadProgress(0);
            setUploadTargetCat('');
            setDragActiveZone(null);
            if (fileInputRefGlobal.current) fileInputRefGlobal.current.value = '';
            if (fileInputRefMaxilla.current) fileInputRefMaxilla.current.value = '';
            if (fileInputRefMandible.current) fileInputRefMandible.current.value = '';
            if (fileInputRefOcclusion.current) fileInputRefOcclusion.current.value = '';
        }
    };

    // Modification rapide de la catégorie d'un fichier existant
    const handleChangeCategory = async (fileToChange, newCat) => {
        try {
            const updated = files.map(f => {
                const match = (f.path && f.path === fileToChange.path) || f.name === fileToChange.name;
                if (match) {
                    return { ...f, category: newCat };
                }
                return f;
            });
            setFiles(updated);
            await syncTransferRecord(updated);
        } catch (err) {
            console.error('Erreur mise à jour catégorie:', err);
        }
    };

    const handleDeleteFile = async (fileToDelete) => {
        if (!window.confirm(`Supprimer le fichier "${fileToDelete.name}" ?`)) return;

        try {
            if (fileToDelete.path) {
                await supabase.storage.from('stl-files').remove([fileToDelete.path]);
            }

            const updatedFiles = files.filter(f => f.path !== fileToDelete.path && f.name !== fileToDelete.name);
            setFiles(updatedFiles);
            await syncTransferRecord(updatedFiles);
        } catch (err) {
            console.error('Erreur suppression fichier:', err);
        }
    };

    const handleCopyTransferLink = async () => {
        try {
            let activeToken = transfer?.token;
            if (!activeToken) {
                const saved = await syncTransferRecord(files);
                activeToken = saved?.token;
            }

            if (!activeToken) {
                alert("Impossible de générer le lien de transfert.");
                return;
            }

            const url = `${window.location.origin}/transfert/${activeToken}`;
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        } catch (e) {
            console.error('Erreur copie lien:', e);
            if (transfer?.token) {
                const url = `${window.location.origin}/transfert/${transfer.token}`;
                window.prompt("Copiez ce lien de transfert :", url);
            }
        }
    };

    const handleChangeExpiration = async (days) => {
        let expDate = null;
        if (days > 0) {
            const d = new Date();
            d.setDate(d.getDate() + days);
            expDate = d.toISOString();
        }

        try {
            if (transfer?.id) {
                const { data } = await supabase
                    .from('case_transfers')
                    .update({ expires_at: expDate, updated_at: new Date().toISOString() })
                    .eq('id', transfer.id)
                    .select()
                    .single();
                if (data) setTransfer(data);
            }
        } catch (e) {
            console.warn('Update expiration failed:', e);
        }
    };

    const totalBytes = files.reduce((acc, f) => acc + (f.size || 0), 0);
    const transferUrl = transfer?.token ? `${window.location.origin}/transfert/${transfer.token}` : '';

    return (
        <div className="case-3d-files-container animate__animated animate__fadeIn">
            {tableError && (
                <div className="alert alert-warning d-flex align-items-center gap-2 mb-3">
                    <FaInfoCircle size={20} className="flex-shrink-0" />
                    <div>
                        <strong>Installation de TransferEno :</strong> Pour activer le partage par lien sécurisé, veuillez exécuter le script SQL <code>setup_transfers.sql</code> dans votre tableau de bord Supabase.
                    </div>
                </div>
            )}

            {/* Top Bar: TransferEno Link Generator */}
            <div className="transfer-eno-banner">
                <div className="transfer-eno-left">
                    <div className="transfer-icon-box">
                        <FaShareAlt size={20} />
                    </div>
                    <div>
                        <div className="d-flex align-items-center gap-2">
                            <h4 className="transfer-eno-title m-0">TransferEno</h4>
                            <span className="badge bg-primary-subtle text-primary border border-primary-subtle fw-semibold" style={{ fontSize: '11px' }}>
                                Partage 3D Sécurisé
                            </span>
                        </div>
                        <p className="transfer-eno-desc m-0">
                            Envoyez directement les fichiers STL à votre sous-traitant avec un lien professionnel aux couleurs d'E-novadent.
                        </p>
                    </div>
                </div>

                <div className="transfer-eno-actions">
                    {files.length > 0 ? (
                        <div className="d-flex align-items-center gap-2">
                            <button
                                type="button"
                                className={`btn ${copied ? 'btn-success' : 'btn-primary'} fw-bold d-flex align-items-center gap-2 px-3 py-2 shadow-sm rounded-3`}
                                onClick={handleCopyTransferLink}
                                title="Copier le lien TransferEno à envoyer au sous-traitant"
                            >
                                {copied ? <FaCheck /> : <FaCopy />}
                                <span>{copied ? 'Lien copié !' : 'Copier le lien TransferEno'}</span>
                            </button>
                            {transferUrl && (
                                <a
                                    href={transferUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-outline-secondary px-2.5 py-2 rounded-3"
                                    title="Ouvrir la page de téléchargement publique"
                                >
                                    <FaExternalLinkAlt size={14} />
                                </a>
                            )}
                        </div>
                    ) : (
                        <span className="text-muted small fst-italic">
                            Ajoutez au moins 1 fichier 3D pour activer le lien.
                        </span>
                    )}
                </div>
            </div>

            {/* Tracking Status */}
            {transfer && files.length > 0 && (
                <div className="transfer-tracking-bar">
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                        <div className="tracking-item">
                            <span className="tracking-label">Statut téléchargement :</span>
                            {transfer.download_count > 0 ? (
                                <span className="badge bg-success-subtle text-success border border-success-subtle fw-bold d-inline-flex align-items-center gap-1.5 px-2.5 py-1.5">
                                    <FaCheckCircle /> Téléchargé {transfer.download_count} fois
                                    {transfer.last_downloaded_at && (
                                        <span className="text-muted fw-normal ms-1">
                                            (Dernier le {new Date(transfer.last_downloaded_at).toLocaleDateString('fr-FR')} à {new Date(transfer.last_downloaded_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })})
                                        </span>
                                    )}
                                </span>
                            ) : (
                                <span className="badge bg-warning-subtle text-warning border border-warning-subtle fw-bold d-inline-flex align-items-center gap-1.5 px-2.5 py-1.5">
                                    <FaClock /> En attente de téléchargement par le fournisseur
                                </span>
                            )}
                        </div>

                        {isAdmin && (
                            <div className="tracking-item d-flex align-items-center gap-1.5">
                                <span className="tracking-label">Validité du lien :</span>
                                <select
                                    className="form-select form-select-sm"
                                    style={{ width: 'auto', display: 'inline-block' }}
                                    value={transfer.expires_at ? 'limited' : 'never'}
                                    onChange={(e) => handleChangeExpiration(e.target.value === 'never' ? 0 : 30)}
                                >
                                    <option value="never">Illimité (ne jamais expirer)</option>
                                    <option value="limited">30 jours</option>
                                </select>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Purge notification */}
            {((order?.status || '').toLowerCase() === 'facturé' || (order?.status || '').toLowerCase() === 'facture') && files.length === 0 && (
                <div className="alert border-0 shadow-sm d-flex align-items-center gap-2 mb-3" style={{ background: '#f0fdf4', color: '#166534', borderRadius: '10px', padding: '12px 16px' }}>
                    <FaCheckCircle className="text-success flex-shrink-0" size={18} />
                    <span className="small">
                        <strong>Cas facturé :</strong> Les fichiers 3D (STL) du cloud ont été automatiquement purgés pour préserver vos quotas de stockage. Les fichiers restent disponibles sur vos postes de travail locaux.
                    </span>
                </div>
            )}

            {/* SECTION DÉPÔT ORGANISÉ DENTAIRE (Haut / Bas / Occlusion) */}
            {isAdmin && (
                <div className="deposit-section mt-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <div className="d-flex align-items-center gap-2">
                            <FaTooth className="text-primary" />
                            <span className="fw-bold text-dark" style={{ fontSize: '0.95rem' }}>
                                Dépôt organisé des scans 3D
                            </span>
                        </div>
                        <span className="text-muted small">
                            Déposez chaque fichier dans sa zone pour un repérage parfait dans le visualiseur
                        </span>
                    </div>

                    {uploading && (
                        <div className="card border-primary-subtle bg-primary-subtle p-3 mb-3 text-center">
                            <div className="d-flex align-items-center justify-content-center gap-2">
                                <FaSpinner className="spin-icon text-primary" size={20} />
                                <strong className="text-primary">
                                    Envoi des fichiers 3D en cours [{uploadTargetCat}]... ({uploadProgress}%)
                                </strong>
                            </div>
                            <div className="progress mt-2 mx-auto" style={{ width: '280px', height: '8px' }}>
                                <div className="progress-bar progress-bar-striped progress-bar-animated bg-primary" style={{ width: `${uploadProgress}%` }}></div>
                            </div>
                        </div>
                    )}

                    {/* Grille des 3 zones de dépôt dédiées */}
                    <div className="deposit-grid">
                        {/* 1. ZONE HAUT (MAXILLAIRE) */}
                        <div
                            className={`deposit-card deposit-maxilla ${dragActiveZone === 'maxillaire' ? 'drag-over' : ''}`}
                            onDragOver={(e) => { e.preventDefault(); setDragActiveZone('maxillaire'); }}
                            onDragLeave={() => setDragActiveZone(null)}
                            onDrop={(e) => {
                                e.preventDefault();
                                setDragActiveZone(null);
                                handleUploadFiles(e.dataTransfer.files, 'maxillaire');
                            }}
                            onClick={() => !uploading && fileInputRefMaxilla.current?.click()}
                        >
                            <input
                                type="file"
                                ref={fileInputRefMaxilla}
                                multiple
                                accept=".stl,.ply,.obj"
                                style={{ display: 'none' }}
                                onChange={(e) => handleUploadFiles(e.target.files, 'maxillaire')}
                            />
                            <div className="deposit-card-header">
                                <span className="deposit-tag tag-maxilla">🦷 Maxillaire (Haut)</span>
                            </div>
                            <div className="deposit-card-body">
                                <div className="deposit-icon-bubble bubble-maxilla">
                                    <FaCloudUploadAlt size={22} />
                                </div>
                                <div className="deposit-card-title">Scan du Haut</div>
                                <div className="deposit-card-desc">
                                    Base + scans supplémentaires (Pre-OP, Scanbody, etc.)
                                </div>
                                <button type="button" className="btn btn-sm btn-outline-primary rounded-pill deposit-btn">
                                    <FaPlus size={11} className="me-1" /> Ajouter au Haut
                                </button>
                            </div>
                        </div>

                        {/* 2. ZONE BAS (MANDIBULE) */}
                        <div
                            className={`deposit-card deposit-mandible ${dragActiveZone === 'mandibule' ? 'drag-over' : ''}`}
                            onDragOver={(e) => { e.preventDefault(); setDragActiveZone('mandibule'); }}
                            onDragLeave={() => setDragActiveZone(null)}
                            onDrop={(e) => {
                                e.preventDefault();
                                setDragActiveZone(null);
                                handleUploadFiles(e.dataTransfer.files, 'mandibule');
                            }}
                            onClick={() => !uploading && fileInputRefMandible.current?.click()}
                        >
                            <input
                                type="file"
                                ref={fileInputRefMandible}
                                multiple
                                accept=".stl,.ply,.obj"
                                style={{ display: 'none' }}
                                onChange={(e) => handleUploadFiles(e.target.files, 'mandibule')}
                            />
                            <div className="deposit-card-header">
                                <span className="deposit-tag tag-mandible">🦷 Mandibule (Bas)</span>
                            </div>
                            <div className="deposit-card-body">
                                <div className="deposit-icon-bubble bubble-mandible">
                                    <FaCloudUploadAlt size={22} />
                                </div>
                                <div className="deposit-card-title">Scan du Bas</div>
                                <div className="deposit-card-desc">
                                    Base + scans supplémentaires (Pre-OP, etc.)
                                </div>
                                <button type="button" className="btn btn-sm btn-outline-purple rounded-pill deposit-btn">
                                    <FaPlus size={11} className="me-1" /> Ajouter au Bas
                                </button>
                            </div>
                        </div>

                        {/* 3. ZONE OCCLUSION */}
                        <div
                            className={`deposit-card deposit-occlusion ${dragActiveZone === 'occlusion' ? 'drag-over' : ''}`}
                            onDragOver={(e) => { e.preventDefault(); setDragActiveZone('occlusion'); }}
                            onDragLeave={() => setDragActiveZone(null)}
                            onDrop={(e) => {
                                e.preventDefault();
                                setDragActiveZone(null);
                                handleUploadFiles(e.dataTransfer.files, 'occlusion');
                            }}
                            onClick={() => !uploading && fileInputRefOcclusion.current?.click()}
                        >
                            <input
                                type="file"
                                ref={fileInputRefOcclusion}
                                multiple
                                accept=".stl,.ply,.obj"
                                style={{ display: 'none' }}
                                onChange={(e) => handleUploadFiles(e.target.files, 'occlusion')}
                            />
                            <div className="deposit-card-header">
                                <span className="deposit-tag tag-occlusion">⚡ Occlusion</span>
                            </div>
                            <div className="deposit-card-body">
                                <div className="deposit-icon-bubble bubble-occlusion">
                                    <FaCloudUploadAlt size={22} />
                                </div>
                                <div className="deposit-card-title">Occlusion (Mordu)</div>
                                <div className="deposit-card-desc">
                                    Première & Deuxième occlusion
                                </div>
                                <button type="button" className="btn btn-sm btn-outline-warning rounded-pill deposit-btn">
                                    <FaPlus size={11} className="me-1" /> Ajouter Occlusion
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Zone de secours / Dépôt global en vrac ou ZIP */}
                    <div
                        className={`deposit-global-strip mt-2 ${dragActiveZone === 'global' ? 'drag-over' : ''}`}
                        onDragOver={(e) => { e.preventDefault(); setDragActiveZone('global'); }}
                        onDragLeave={() => setDragActiveZone(null)}
                        onDrop={(e) => {
                            e.preventDefault();
                            setDragActiveZone(null);
                            handleUploadFiles(e.dataTransfer.files, null);
                        }}
                        onClick={() => !uploading && fileInputRefGlobal.current?.click()}
                    >
                        <input
                            type="file"
                            ref={fileInputRefGlobal}
                            multiple
                            accept=".stl,.ply,.obj,.zip,.rar,.7z"
                            style={{ display: 'none' }}
                            onChange={(e) => handleUploadFiles(e.target.files, null)}
                        />
                        <FaCloudUploadAlt size={16} className="text-muted" />
                        <span className="small text-muted">
                            Ou glissez ici vos fichiers en vrac ou une archive <strong>.ZIP (Exocad / 3Shape)</strong> — détection automatique
                        </span>
                    </div>
                </div>
            )}

            {/* Files List */}
            <div className="files-3d-list-card mt-3">
                <div className="files-3d-header d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-2">
                        <FaCube className="text-primary" />
                        <h5 className="m-0 fw-bold">
                            Fichiers du cas ({files.length})
                        </h5>
                    </div>
                    {files.length > 0 && (
                        <div className="d-flex align-items-center gap-2">
                            {files.some(canViewFile) && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-primary fw-bold d-inline-flex align-items-center gap-1.5 rounded-pill px-3 shadow-sm"
                                    onClick={() => setViewerFile(files.find(canViewFile))}
                                    title="Ouvrir le visualisateur 3D style Medit"
                                >
                                    <FaEye size={13} />
                                    <span>Visualiser en 3D</span>
                                </button>
                            )}
                            <span className="badge bg-light text-dark border px-2.5 py-1.5 fw-semibold">
                                Poids total : {formatFileSize(totalBytes)}
                            </span>
                        </div>
                    )}
                </div>

                {loading ? (
                    <div className="p-4 text-center text-muted">
                        <FaSpinner className="spin-icon me-2" /> Chargement des fichiers...
                    </div>
                ) : files.length === 0 ? (
                    <div className="p-4 text-center text-muted">
                        <FaCube size={36} className="text-muted opacity-50 mb-2" />
                        <p className="m-0">Aucun fichier 3D n'a encore été ajouté à ce cas.</p>
                        <small className="text-muted">Déposez vos scans ci-dessus pour lancer la visualisation et le partage TransferEno.</small>
                    </div>
                ) : (
                    <div className="files-3d-table-wrapper">
                        <table className="files-3d-table">
                            <thead>
                                <tr>
                                    <th>Fichier</th>
                                    <th style={{ width: '190px' }}>Arcade / Catégorie</th>
                                    <th>Taille</th>
                                    <th>Date d'ajout</th>
                                    <th className="text-end">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {files.map((file, idx) => {
                                    const isZip = file.name.endsWith('.zip') || file.name.endsWith('.rar');
                                    const currentCat = file.category || detectCategory(file.name);
                                    const meta = CATEGORY_META[currentCat] || CATEGORY_META.autres;

                                    return (
                                        <tr key={file.path || idx}>
                                            <td className="fw-semibold text-dark">
                                                <div className="d-flex align-items-center gap-2">
                                                    {isZip ? (
                                                        <FaFileArchive className="text-warning flex-shrink-0" size={18} />
                                                    ) : (
                                                        <FaCube className="text-primary flex-shrink-0" size={18} />
                                                    )}
                                                    <span className="text-break">{file.name}</span>
                                                </div>
                                            </td>

                                            {/* Colonne Catégorie avec Sélecteur modifiable */}
                                            <td>
                                                {isAdmin && !isZip ? (
                                                    <select
                                                        className="form-select form-select-sm category-select"
                                                        value={currentCat}
                                                        onChange={(e) => handleChangeCategory(file, e.target.value)}
                                                        title="Changer l'arcade pour le visualisateur 3D"
                                                        style={{
                                                            fontSize: '12px',
                                                            fontWeight: '600',
                                                            borderRadius: '8px',
                                                            padding: '3px 8px'
                                                        }}
                                                    >
                                                        <option value="maxillaire">🦷 Haut (Maxillaire)</option>
                                                        <option value="mandibule">🦷 Bas (Mandibule)</option>
                                                        <option value="occlusion">⚡ Occlusion</option>
                                                        <option value="autres">📁 Autre</option>
                                                    </select>
                                                ) : (
                                                    <span
                                                        className={`badge ${meta.badgeClass}`}
                                                        style={{ fontSize: '11px', ...(meta.style || {}) }}
                                                    >
                                                        {meta.icon} {meta.label}
                                                    </span>
                                                )}
                                            </td>

                                            <td className="text-muted small">{formatFileSize(file.size)}</td>
                                            <td className="text-muted small">
                                                {file.uploadedAt ? new Date(file.uploadedAt).toLocaleDateString('fr-FR') : '-'}
                                            </td>
                                            <td className="text-end">
                                                <div className="d-flex justify-content-end gap-1.5">
                                                    {canViewFile(file) && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1 rounded-2"
                                                            onClick={() => setViewerFile(file)}
                                                            title="Ouvrir dans le visualisateur 3D"
                                                        >
                                                            <FaEye size={12} />
                                                            <span className="d-none d-sm-inline">Voir 3D</span>
                                                        </button>
                                                    )}
                                                    <a
                                                        href={file.url}
                                                        download={file.name}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1 rounded-2"
                                                        title="Télécharger ce fichier"
                                                    >
                                                        <FaDownload size={12} />
                                                    </a>
                                                    {isAdmin && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-danger rounded-2"
                                                            onClick={() => handleDeleteFile(file)}
                                                            title="Supprimer"
                                                        >
                                                            <FaTrash size={12} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal Visualisateur 3D Style Medit */}
            {viewerFile && (
                <STLViewerModal
                    files={files}
                    initialFile={viewerFile}
                    onClose={() => setViewerFile(null)}
                />
            )}
        </div>
    );
}

export default Case3DFiles;
